/* Custom banner slider — dependency-free peek carousel (loop, drag, autoplay, dots) */
class CustomBannerSlider extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-cbs-viewport]');
    this.track = this.querySelector('[data-cbs-track]');
    if (!this.viewport || !this.track) return;

    this.slides = Array.from(this.track.children);
    this.count = this.slides.length;
    if (this.count === 0) return;

    this.dots = Array.from(this.querySelectorAll('[data-cbs-dot]'));
    this.prevBtn = this.querySelector('[data-cbs-prev]');
    this.nextBtn = this.querySelector('[data-cbs-next]');

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.loop = this.dataset.loop === 'true' && this.count > 1;
    this.autoplayEnabled = this.dataset.autoplay === 'true' && this.count > 1 && !this.reduceMotion;
    this.delay = (parseFloat(this.dataset.autoplaySpeed) || 6) * 1000;

    if (this.loop) this.buildClones();

    this.allSlides = Array.from(this.track.children);
    this.pos = this.loop ? 1 : 0;
    this.dragDistance = 0;

    this.measure();
    this.goTo(this.pos, false);
    this.bind();
    this.syncUi();
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.resizeObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  /* A clone of the last slide sits before the first, and vice versa, so the
     wrap-around is a silent re-seat rather than a rewind animation. */
  buildClones() {
    const first = this.slides[0].cloneNode(true);
    const last = this.slides[this.count - 1].cloneNode(true);
    [first, last].forEach((clone) => {
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.cbsClone = 'true';
      clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
    });
    this.track.append(first);
    this.track.prepend(last);
  }

  measure() {
    const slide = this.allSlides[0];
    this.slideWidth = slide.getBoundingClientRect().width;
    this.gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    this.step = this.slideWidth + this.gap;
  }

  goTo(pos, animate = true) {
    this.pos = pos;
    this.track.style.transitionDuration = animate && !this.reduceMotion ? '' : '0ms';
    this.track.style.transform = `translate3d(${-pos * this.step}px, 0, 0)`;
    if (!animate) {
      // Force a reflow so the next transform actually animates.
      void this.track.offsetHeight;
      this.track.style.transitionDuration = '';
    }
    this.syncUi();
  }

  get realIndex() {
    if (!this.loop) return this.pos;
    return ((this.pos - 1) % this.count + this.count) % this.count;
  }

  next() {
    if (!this.loop && this.pos >= this.count - 1) return;
    this.goTo(this.pos + 1);
  }

  prev() {
    if (!this.loop && this.pos <= 0) return;
    this.goTo(this.pos - 1);
  }

  select(index) {
    this.goTo(this.loop ? index + 1 : index);
  }

  onTransitionEnd = (event) => {
    if (event.target !== this.track || event.propertyName !== 'transform') return;
    if (!this.loop) return;
    if (this.pos === 0) this.goTo(this.count, false);
    else if (this.pos === this.count + 1) this.goTo(1, false);
  };

  syncUi() {
    const index = this.realIndex;
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    this.allSlides.forEach((slide, i) => {
      slide.toggleAttribute('inert', i !== this.pos);
    });
    if (!this.loop) {
      if (this.prevBtn) this.prevBtn.disabled = this.pos <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.pos >= this.count - 1;
    }
  }

  /* ---- autoplay ---- */
  startAutoplay() {
    if (!this.autoplayEnabled) return;
    this.stopAutoplay();
    this.timer = setInterval(() => this.next(), this.delay);
  }

  stopAutoplay() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  onVisibility = () => {
    if (document.hidden) this.stopAutoplay();
    else this.startAutoplay();
  };

  /* ---- events ---- */
  bind() {
    this.track.addEventListener('transitionend', this.onTransitionEnd);
    this.prevBtn?.addEventListener('click', () => this.prev());
    this.nextBtn?.addEventListener('click', () => this.next());
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.select(i)));

    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());
    document.addEventListener('visibilitychange', this.onVisibility);

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); this.next(); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.prev(); }
    });

    this.resizeObserver = new ResizeObserver(() => {
      this.measure();
      this.goTo(this.pos, false);
    });
    this.resizeObserver.observe(this.viewport);

    // Theme editor: jump to a slide when its block is selected, and hold there.
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    this.viewport.addEventListener('pointerdown', this.onPointerDown);
    // Suppress the click that follows a real drag, so swiping never opens the link.
    this.viewport.addEventListener('click', (event) => {
      if (Math.abs(this.dragDistance) > 5) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);
  }

  onBlockSelect = (event) => {
    const index = this.slides.indexOf(event.target);
    if (index === -1) return;
    this.stopAutoplay();
    this.select(index);
  };

  onBlockDeselect = (event) => {
    if (this.slides.indexOf(event.target) === -1) return;
    this.startAutoplay();
  };

  onPointerDown = (event) => {
    if (event.button !== 0 || this.count < 2) return;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.dragDistance = 0;
    this.dragging = false;
    this.stopAutoplay();
    this.viewport.setPointerCapture(event.pointerId);
    this.viewport.addEventListener('pointermove', this.onPointerMove);
    this.viewport.addEventListener('pointerup', this.onPointerUp);
    this.viewport.addEventListener('pointercancel', this.onPointerUp);
  };

  onPointerMove = (event) => {
    const dx = event.clientX - this.startX;
    const dy = event.clientY - this.startY;
    // Let a mostly-vertical gesture scroll the page instead of moving the track.
    if (!this.dragging && Math.abs(dx) < Math.abs(dy)) return;
    if (!this.dragging && Math.abs(dx) < 5) return;

    this.dragging = true;
    this.dragDistance = dx;
    this.classList.add('is-dragging');
    this.viewport.classList.add('is-dragging');
    this.track.style.transitionDuration = '0ms';
    this.track.style.transform = `translate3d(${-this.pos * this.step + dx}px, 0, 0)`;
  };

  onPointerUp = (event) => {
    this.viewport.releasePointerCapture?.(event.pointerId);
    this.viewport.removeEventListener('pointermove', this.onPointerMove);
    this.viewport.removeEventListener('pointerup', this.onPointerUp);
    this.viewport.removeEventListener('pointercancel', this.onPointerUp);
    this.viewport.classList.remove('is-dragging');
    this.classList.remove('is-dragging');

    if (this.dragging) {
      const threshold = Math.max(40, this.slideWidth * 0.12);
      if (this.dragDistance <= -threshold) this.next();
      else if (this.dragDistance >= threshold) this.prev();
      else this.goTo(this.pos);
    }

    this.dragging = false;
    this.startAutoplay();
    // Clear the click guard after the click event has had a chance to fire.
    setTimeout(() => { this.dragDistance = 0; }, 0);
  };
}

if (!customElements.get('custom-banner-slider')) {
  customElements.define('custom-banner-slider', CustomBannerSlider);
}
